import { Button, Div } from '@codesuma/baseline'
import calendar, { IDayCell } from '../../../services/calendar'
import { CalendarSystem } from '../../../services/settings'
import tasks, { ITask } from '../../../services/tasks'
import rating from '../../../services/rating'
import phoneReminders, { IPhoneReminder } from '../../../services/phone-reminders'
import { G_MONTHS, J_MONTHS } from '../../../configs/names'
import { toFaDigits } from '../../../helpers/digits'
import { solarYear } from '../../../helpers/year'
import { PhoneMark } from '../../../components/phone-mark'
import { Section } from './section'
import style from './index.module.css'

// Material Design icons (Apache 2.0), inheriting currentColor.
const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
const PLUS_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/></svg>'

// A to-do and the day it sits on. The day comes along because these lists run
// across days, and every row has to say which one it belongs to. On an iPhone a
// row can be a reminder read from the Reminders app instead of a task.
export type ITaskRow = { task: ITask, cell: IDayCell } | { reminder: IPhoneReminder, cell: IDayCell }

const key = (y: number, m: number, d: number) => y * 10000 + m * 100 + d

// Everything still open on a day already gone, oldest first. Not read off the
// grid: these reach back behind whatever month is on screen, so each row's day
// is worked out from the task's own date. Ticked-off ones are not behind on
// anything and stay out.
export const overdueRows = (): ITaskRow[] => {
    const now = calendar.today()
    const from = key(now.gy, now.gm, now.gd)
    return tasks.all()
        .filter(t => !t.done && key(t.gy, t.gm, t.gd) < from)
        .sort((a, b) => key(a.gy, a.gm, a.gd) - key(b.gy, b.gm, b.gd))
        .map(task => ({ task, cell: calendar.dayAt(task.gy, task.gm, task.gd, 0) }))
}

// Today and the rest of the month, ticked-off ones included — what is still
// ahead of you, reading on from the overdue list above it. Taken straight off
// the month's own days, so the month on screen decides where it ends, and empty
// on every month but today's, which is the only one where "ahead" means
// anything. Each day's reminders from the Reminders app follow its tasks.
export const aheadRows = (inMonth: IDayCell[]): ITaskRow[] => {
    const from = inMonth.findIndex(c => c.isToday)
    if (from < 0) return []
    return inMonth.slice(from).flatMap((cell): ITaskRow[] => [
        ...tasks.forDay(cell).map(task => ({ task, cell })),
        ...phoneReminders.forDay(cell).map(reminder => ({ reminder, cell })),
    ])
}

// Only as much of the date as it takes to place the day. The lists using this
// sit on the month today is in, so one of its days is a bare number; a row from
// another month takes the month name with it, and the year too once that
// differs as well. `withMonth` names the month even on this month's days.
export const dayStamp = (c: IDayCell, system: CalendarSystem, withMonth = false) => {
    const now = calendar.today()
    const g = system === 'gregorian'
    const [y, m, d] = g ? [c.gy, c.gm, c.gd] : [c.jy, c.jm, c.jd]
    const [ny, nm] = g ? [now.gy, now.gm] : [now.jy, now.jm]
    const parts = [g ? String(d) : toFaDigits(d)]
    if (withMonth || y !== ny || m !== nm) parts.push((g ? G_MONTHS : J_MONTHS)[m - 1])
    if (y !== ny) parts.push(g ? String(y) : solarYear(y))
    return parts.join(' ')
}

// One of those lists under the grid: a ring to tick the task off, its title,
// and the day it sits on at the far end.
//
// Two taps and that is the whole row, the same pair the day page's own list
// has: the ring marks it done right here, and the title opens the day it sits
// on, which is where renaming, the hour, the reminder and the delete live.
//
// `canAdd` puts one more line under the rows, the way in to a task that is not
// there yet; it emits 'add-task' and the page decides where that goes. Emits
// 'day-open' for a row's own day. The header and the scrolling are the
// Section's — see there.
//
// `withMonth` names the month on every row, not only the ones from another
// month: the overdue list runs back across months, and a bare day number in it
// reads as this month's when it may not be.
export const TaskList = (
    title: string,
    system: CalendarSystem,
    rows: ITaskRow[],
    scroller: HTMLElement,
    { canAdd = false, withMonth = false } = {},
) => {
    const base = Section(title, scroller)
    // The Gregorian slide is LTR for its grid, but a task is the user's own
    // prose — keep it reading right-to-left like the rest of the app.
    if (system === 'gregorian') base.attr('dir', 'rtl')

    for (const entry of rows) {
        const { cell } = entry
        const row = Div()
        row.addClass(style.task)

        if ('reminder' in entry) {
            // One of the Reminders app's. Its ring says whether it is done but
            // does not change it — that happens in the Reminders app — and its
            // title opens its day, the way a task's does.
            const { reminder } = entry
            row.toggleClass(style.taskDone, reminder.done)
            const ring = Div()
            ring.addClass(style.tick)
            ring.attr('aria-hidden', 'true')
            ring.html(CHECK_SVG)
            row.append(ring)

            const name = Button(reminder.title)
            name.addClass(style.taskTitle)
            name.append(PhoneMark())
            name.on('click', () => base.emit('day-open', cell))
            row.append(name)
        } else {
            const { task } = entry
            // The ring the day page ticks, and it ticks here too. The row
            // redraws itself as well as saving: the store's broadcast rebuilds
            // the slide, but the mark should be on before anything else happens.
            const tick = Button()
            tick.addClass(style.tick)
            tick.html(CHECK_SVG)
            const syncDone = () => {
                row.toggleClass(style.taskDone, task.done)
                tick.attr('aria-pressed', String(task.done))
                tick.attr('aria-label', task.done ? 'انجام‌نشده' : 'انجام شد')
            }
            tick.on('click', () => {
                task.done = !task.done
                tasks.update(task.id, { done: task.done })
                syncDone()
                if (task.done) rating.goodMoment()
            })
            syncDone()
            row.append(tick)

            const name = Button(task.title)
            name.addClass(style.taskTitle)
            name.on('click', () => base.emit('day-open', cell))
            row.append(name)
        }

        const day = Div(dayStamp(cell, system, withMonth))
        day.addClass(style.taskDate)
        row.append(day)

        base.list.append(row)
    }

    // The offer under the list, when the caller wanted one.
    if (canAdd) {
        const add = Button()
        add.addClass(style.addRow)
        add.html(`${PLUS_SVG}<span>افزودن کاری دیگر</span>`)
        add.on('click', () => base.emit('add-task'))
        base.list.append(add)
    }

    return base
}
